import { useCallback, useState } from 'react';
import { suggestKits } from '../../api/kits';

/**
 * Podmiana pojedynczego produktu w zestawie — „pokaż mi coś innego w tej pozycji".
 *
 * ═══ DLACZEGO PRZEZ SILNIK, A NIE PODMIANKA W PRZEGLĄDARCE ═══
 * Kusi, żeby po prostu pobrać inny model z listingu i podmienić wiersz. Byłoby to
 * szybsze i całkowicie błędne: cena pozycji to jedno, ale zestaw ma jeszcze sumę
 * produktów, koszt dostawy liczony z wagi i palet, sprawdzoną kompatybilność pary
 * falownik–magazyn oraz dobór BMS. Podmiana w przeglądarce zostawiłaby te wartości
 * z POPRZEDNIEGO produktu — karta pokazywałaby nowy moduł ze starą dostawą
 * i starym potwierdzeniem zgodności.
 *
 * Dlatego prosimy silnik o zestaw jeszcze raz: pozostałe kategorie PRZYPINAMY
 * (zostają dokładnie te same produkty), a w podmienianej kategorii WYKLUCZAMY to,
 * co użytkownik już widział. Wracają prawdziwe liczby, nie sklejka.
 *
 * Zapytanie jest przy tym tanie: przypięcie zwija dwie kategorie do jednego
 * kandydata, więc silnik nie przeszukuje całej przestrzeni jak przy pierwszym doborze.
 *
 * HISTORIA jest stosem, nie pojedynczą wartością — po trzech podmianach cofanie
 * ma wracać krok po kroku, a nie od razu na sam początek.
 */
export const usePodmianaProduktu = (wariantPoczatkowy, requestPayload) => {
  const [wariant, setWariant] = useState(wariantPoczatkowy);
  const [historia, setHistoria] = useState([]);
  const [trwa, setTrwa] = useState(null);      // kategoria w trakcie podmiany
  const [brakZamiennika, setBrakZamiennika] = useState(null);

  const modelWKategorii = useCallback((w, kategoria) =>
    (w.items || []).find((i) => i.category === kategoria)?.model_id ?? null, []);

  const podmien = useCallback(async (kategoria) => {
    if (!requestPayload) return;
    setTrwa(kategoria);
    setBrakZamiennika(null);

    // Modele już odrzucone w tej kategorii: bieżący + wszystkie z historii.
    const odrzucone = [
      modelWKategorii(wariant, kategoria),
      ...historia.map((h) => modelWKategorii(h, kategoria)),
    ].filter((id) => id != null);

    const payload = JSON.parse(JSON.stringify(requestPayload));
    (payload.categories || []).forEach((kat) => {
      const wejscie = payload[kat === 'panels' ? 'panels' : kat];
      if (!wejscie) return;
      if (kat === kategoria) {
        wejscie.exclude_model_ids = [...new Set(odrzucone)];
        wejscie.pinned_model_id = null;
      } else {
        // Pozostałe kategorie zostają BEZ ZMIAN — inaczej „zmień moduł" potrafiłoby
        // przy okazji podmienić falownik i użytkownik nie wiedziałby, co kliknął.
        const id = modelWKategorii(wariant, kat);
        if (id != null) wejscie.pinned_model_id = id;
      }
    });

    try {
      const res = await suggestKits(payload);
      const nowy = (res?.variants || [])[0];
      if (!nowy) {
        setBrakZamiennika(kategoria);
        return;
      }
      setHistoria((h) => [...h, wariant]);
      setWariant(nowy);
    } catch {
      setBrakZamiennika(kategoria);
    } finally {
      setTrwa(null);
    }
  }, [wariant, historia, requestPayload, modelWKategorii]);

  const cofnij = useCallback(() => {
    setHistoria((h) => {
      if (!h.length) return h;
      setWariant(h[h.length - 1]);
      setBrakZamiennika(null);
      return h.slice(0, -1);
    });
  }, []);

  return {
    wariant,
    podmien,
    cofnij,
    mozliwyPowrot: historia.length > 0,
    trwaPodmiana: trwa,
    brakZamiennika,
    wyczyscKomunikat: () => setBrakZamiennika(null),
  };
};

export default usePodmianaProduktu;
